import { images } from "src/assets/images/images";
import {
  API_RESULT,
  BusinessUnit,
  CHANNELS,
  Gender,
  HebrewNounsGenderMap,
  HebrowNouns,
  URL_SOURCE_TYPE,
  UserPlatform,
} from "./enums";
import { store } from "src/redux/store";
import { setPopupInfo } from "src/redux/slices/popupSlice/popupSlice";
import { updateOnboardingAnalytics } from "src/redux/slices/appSlice/AppApi";
import {
  PAYMENT_METHOD_INDICATION,
  PostMessageData,
  TAX_REFUND_CONDITION,
  TaxRefundStatus,
  UpdateOnboardingAnalytics,
} from "./types";
import { POPUP_TYPE } from "./enums";
import {
  unitNumbers,
  tensNumbers,
  hundredsNumbers,
  monthNames,
  ordinalNumbers,
} from "./consts";

export const __DEV__ = process.env.NODE_ENV === "development";

const isMobile = () => window.innerWidth < 768;
const isDesktop = () => window.innerWidth >= 768;

export const formatPhoneNumber = (phoneNumber: string): string => {
  // Remove any non-digit characters from the phone number
  const cleanedPhoneNumber = phoneNumber.replace(/\D/g, "");

  // Check if the phone number starts with '972' or '+972'
  if (cleanedPhoneNumber.startsWith("972")) {
    // Remove the '972' prefix
    const formattedNumber = cleanedPhoneNumber.slice(3);
    // Insert a dash after the third digit
    return formattedNumber.replace(/(\d{3})(\d{7})/, "$1-$2");
  }

  // If the phone number doesn't start with '972' or '+972', assume it's a local number
  // Insert a dash after the third digit
  return cleanedPhoneNumber.replace(/(\d{3})(\d{7})/, "$1-$2");
};

export const isValidIsraeliID = (idInput: string): boolean => {
  var id = String(idInput).trim();
  id = id.length < 9 ? ("00000000" + id).slice(-9) : id;
  return (
    Array.from(id, Number).reduce((counter, digit, i) => {
      const step = digit * ((i % 2) + 1);
      return counter + (step > 9 ? step - 9 : step);
    }) %
      10 ===
    0
  );
};
export const truncateText = (text: string, maxLength: number) => {
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + "...";
  }
  return text;
};

const screenHeight = window.innerHeight;

const warningPopupScreenPercentage = 98;
export const warningPopupHeight =
  screenHeight * (warningPopupScreenPercentage / 100);

const leadsTransferringPercentage = 95;
export const leadsTransferringPopupHeight =
  screenHeight * (leadsTransferringPercentage / 100);

export const sentPostMessage = (data: PostMessageData) => {
  console.log("<<  Sent post message to parent --> ", data);
  window.parent.postMessage(data, "*"); // '*' allows messages from any origin
};
export const getChannelId = (hostUrl: string): number => {
  const lowerCaseHostUrl = hostUrl.toLowerCase();
  switch (lowerCaseHostUrl) {
    case URL_SOURCE_TYPE.ENSURE:
      return CHANNELS.ENSURE;
    case URL_SOURCE_TYPE.DIGITAL:
      return CHANNELS.DIGITAL;
    case URL_SOURCE_TYPE.DIGITAL_AGENT:
      return CHANNELS.DIGITAL_AGENT;
    case URL_SOURCE_TYPE.SMS:
      return CHANNELS.SMS;
    case URL_SOURCE_TYPE.PFS_REP:
      return CHANNELS.PFS_REP;
    case URL_SOURCE_TYPE.MARKETING:
      return CHANNELS.MARKETING;
    case URL_SOURCE_TYPE.CAMPAIGN:
      return CHANNELS.CAMPAIGN;
    case URL_SOURCE_TYPE.REP_MANUAL:
      return CHANNELS.REP_MANUAL;
    case URL_SOURCE_TYPE.SMS_SHARE:
      return CHANNELS.SMS_SHARE;
    case URL_SOURCE_TYPE.APP:
      return CHANNELS.APP;
    default:
      return CHANNELS.ENSURE;
  }
};
export const getPaymentMethodIndication = (
  paymentMethodIndication: PAYMENT_METHOD_INDICATION,
  strings: any,
  otherCards: boolean
): { text: string; ariaLabel: string } => {
  switch (paymentMethodIndication) {
    case PAYMENT_METHOD_INDICATION.CARD_BRAND_NOT_AUTHORIZED:
      return !otherCards
        ? {
            text: strings.t_active_credit_card_brand_not_authorized,
            ariaLabel: strings.acc_t_active_credit_card_brand_not_authorized,
          }
        : {
            text: strings.t_active_credit_card_brand_not_authorized_other_cards,
            ariaLabel:
              strings.acc_t_active_credit_card_brand_not_authorized_other_cards,
          };
    case PAYMENT_METHOD_INDICATION.CARD_WILL_EXPIRE:
      return !otherCards
        ? {
            text: strings.t_active_credit_card_will_expire,
            ariaLabel: strings.acc_t_active_credit_card_will_expire,
          }
        : {
            text: strings.t_active_credit_card_will_expire_other_cards,
            ariaLabel: strings.acc_t_active_credit_card_will_expire_other_cards,
          };
    case PAYMENT_METHOD_INDICATION.PERSONAL_ID_MISMATCH:
      return !otherCards
        ? {
            text: strings.t_active_credit_personal_id_mismatch,
            ariaLabel: strings.acc_t_active_credit_personal_id_mismatch,
          }
        : {
            text: strings.t_active_credit_personal_id_mismatch_other_cards,
            ariaLabel:
              strings.acc_t_active_credit_personal_id_mismatch_other_cards,
          };
    case PAYMENT_METHOD_INDICATION.GENERAL_ERROR:
      return {
        text: strings.t_active_credit_general_error,
        ariaLabel: strings.acc_t_active_credit_general_error,
      };
    case PAYMENT_METHOD_INDICATION.NOT_ISRAELI_CARD:
      return !otherCards
        ? {
            text: strings.t_active_credit_not_israeli_card,
            ariaLabel: strings.acc_t_active_credit_not_israeli_card,
          }
        : {
            text: strings.t_active_credit_not_israeli_card_other_cards,
            ariaLabel: strings.acc_t_active_credit_not_israeli_card_other_cards,
          };
    case PAYMENT_METHOD_INDICATION.DEBIT_CARD:
      return !otherCards
        ? {
            text: strings.t_active_credit_debit_card,
            ariaLabel: strings.acc_t_active_credit_debit_card,
          }
        : {
            text: strings.t_active_credit_debit_card_other_cards,
            ariaLabel: strings.acc_t_active_credit_debit_card_other_cards,
          };
    case PAYMENT_METHOD_INDICATION.NOT_ISRAELI_AND_DEBIT_CARD:
      return !otherCards
        ? {
            text: strings.t_active_credit_not_israeli_and_debit_card,
            ariaLabel: strings.acc_t_active_credit_not_israeli_and_debit_card,
          }
        : {
            text: strings.t_active_credit_not_israeli_and_debit_card_other_cards,
            ariaLabel:
              strings.acc_t_active_credit_not_israeli_and_debit_card_other_cards,
          };
    default:
      return !otherCards
        ? {
            text: strings.t_active_credit_card_general_error,
            ariaLabel: strings.acc_t_active_credit_card_general_error,
          }
        : {
            text: strings.t_active_credit_card_general_error_other_cards,
            ariaLabel:
              strings.acc_t_active_credit_card_general_error_other_cards,
          };
  }
};

export const getPaymentMethodIndicationFromCheckFxEligibility = (
  paymentMethodIndication: PAYMENT_METHOD_INDICATION,
  strings: any,
  otherCards: boolean
): { text: string; ariaLabel: string } => {
  switch (paymentMethodIndication) {
    case PAYMENT_METHOD_INDICATION.CARD_BRAND_NOT_AUTHORIZED:
      return !otherCards
        ? {
            text: strings.t_active_credit_card_fx_brand_not_authorized,
            ariaLabel: strings.acc_t_active_credit_card_fx_brand_not_authorized,
          }
        : {
            text: strings.t_active_credit_card_fx_brand_not_authorized_other_cards,
            ariaLabel:
              strings.acc_t_active_credit_card_fx_brand_not_authorized_other_cards,
          };
    case PAYMENT_METHOD_INDICATION.CARD_WILL_EXPIRE:
      return !otherCards
        ? {
            text: strings.t_active_credit_card_fx_will_expire,
            ariaLabel: strings.acc_t_active_credit_card_fx_will_expire,
          }
        : {
            text: strings.t_active_credit_card_fx_will_expire_other_cards,
            ariaLabel:
              strings.acc_t_active_credit_card_fx_will_expire_other_cards,
          };
    case PAYMENT_METHOD_INDICATION.PERSONAL_ID_MISMATCH:
      return !otherCards
        ? {
            text: strings.t_active_credit_card_fx_personal_id_mismatch,
            ariaLabel: strings.acc_t_active_credit_card_fx_personal_id_mismatch,
          }
        : {
            text: strings.t_active_credit_card_fx_personal_id_mismatch_other_cards,
            ariaLabel:
              strings.acc_t_active_credit_card_fx_personal_id_mismatch_other_cards,
          };
    case PAYMENT_METHOD_INDICATION.GENERAL_ERROR:
      return {
        text: strings.t_active_credit_card_fx_general_error,
        ariaLabel: strings.acc_t_active_credit_card_fx_general_error,
      };
    case PAYMENT_METHOD_INDICATION.NOT_ISRAELI_CARD:
      return !otherCards
        ? {
            text: strings.t_active_credit_card_fx_not_israeli_card,
            ariaLabel: strings.acc_t_active_credit_card_fx_not_israeli_card,
          }
        : {
            text: strings.t_active_credit_card_fx_not_israeli_card_other_cards,
            ariaLabel:
              strings.acc_t_active_credit_card_fx_not_israeli_card_other_cards,
          };
    case PAYMENT_METHOD_INDICATION.DEBIT_CARD:
      return !otherCards
        ? {
            text: strings.t_active_credit_card_fx_debit_card,
            ariaLabel: strings.acc_t_active_credit_card_fx_debit_card,
          }
        : {
            text: strings.t_active_credit_card_fx_debit_card_other_cards,
            ariaLabel:
              strings.acc_t_active_credit_card_fx_debit_card_other_cards,
          };
    case PAYMENT_METHOD_INDICATION.NOT_ISRAELI_AND_DEBIT_CARD:
      return !otherCards
        ? {
            text: strings.t_active_credit_card_fx_not_israeli_and_debit_card,
            ariaLabel:
              strings.acc_t_active_credit_card_fx_not_israeli_and_debit_card,
          }
        : {
            text: strings.t_active_credit_card_fx_not_israeli_and_debit_card_other_cards,
            ariaLabel:
              strings.acc_t_active_credit_card_fx_not_israeli_and_debit_card_other_cards,
          };
    default:
      return !otherCards
        ? {
            text: strings.t_active_credit_card_fx_general_error,
            ariaLabel: strings.acc_t_active_credit_card_fx_general_error,
          }
        : {
            text: strings.t_active_credit_card_fx_general_error,
            ariaLabel:
              strings.acc_t_active_credit_card_fx_general_error_other_cards,
          };
  }
};

export const handleShare = async (title: string, text: string, url: string) => {
  if (navigator.share) {
    try {
      await navigator.share({
        title,
        text,
        url,
      });
      console.log("Content shared successfully");
    } catch (error) {
      console.error("Error sharing content", error);
    }
  } else {
    console.warn("Web Share API is not supported on this browser");

    // Fallback for sharing via WhatsApp on desktop
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
      `${text} ${url}`
    )}`;

    if (navigator.userAgent.includes("Mobile")) {
      // On mobile, attempt to open WhatsApp if available
      window.location.href = whatsappUrl;
    } else {
      // On desktop, open WhatsApp Web
      window.open(whatsappUrl, "_blank");
    }
  }
};

export const showPopupWithMessage = (
  title: string,
  body: string,
  strings: any,
  withButton: boolean = false,
  errorStatus: number = 0,
  okBtnText: string = strings.t_error_popup_cant_find_button,
  img: string = images.agent,
  mobileImg: string = images.errMobile,
  withBuyPolicyBtn: boolean = false,
  buyPolicyBtnText: string = strings.t_buy_policy_button
) => {
  const currentSource = window.location.pathname
    .split("/")[1]
    .toLocaleLowerCase();
  const isDigitalOrDigitalAgentPopup =
    currentSource === URL_SOURCE_TYPE.DIGITAL_AGENT ||
    currentSource === URL_SOURCE_TYPE.DIGITAL;

  if (!isDigitalOrDigitalAgentPopup) {
     console.log("strings.t_buy_policy_button", buyPolicyBtnText);
    store.dispatch(
      setPopupInfo({
        show: true,
        type: POPUP_TYPE.WARNING,
        title: title,
        subTitle: "",
        body: body,
        okBtnText: okBtnText,
        img: img,
        mobileImg: mobileImg,
        withButton: withButton,
        hideCloseBtn: false,
        errorStatus: errorStatus,
        withBuyPolicyBtn: withBuyPolicyBtn,
        buyPolicyBtnText: buyPolicyBtnText,
      })
    );
  }
};

const sendPostMessageToParentWithData = (
  message: string,
  responseCode: number
) => {
  const data = {
    status: API_RESULT.FAILED,
    message: message,
    responseCode: responseCode.toString(),
  };
  sentPostMessage(data);
};
export const sendPostMessageFromCGIframeToParent = (data: any) => {
  sentPostMessage(data);
};
export const isEnsureOrPfsRepOrPfsRepManual = (currentSource: string) => {
  return (
    currentSource === URL_SOURCE_TYPE.ENSURE ||
    currentSource === URL_SOURCE_TYPE.PFS_REP ||
    currentSource === URL_SOURCE_TYPE.REP_MANUAL
  );
};

export const getTitleByResponseCode = (responseCode: number, strings: any) => {
  switch (responseCode) {
    case 7:
      return strings.t_popupTitleCustomerNotFound;
    case 8:
      return strings.t_popupTitleNoInsuranceMarketing;
  }
};

export const getBodyByResponseCode = (responseCode: number, strings: any) => {
  switch (responseCode) {
    case 7:
      return strings.t_popupTextCustomerNotFound;
    case 8:
      return strings.t_popupTextNoInsuranceMarketing;
  }
};

export const handleResponseCode = (
  responseData: any,
  currentSource: string,
  hostUrl: string,
  isDigitalOrAgent: boolean,
  strings: any
) => {
  const lowerCaseCurrentSource = currentSource.toLowerCase();
  const isSmsOrCampaignOrMarketingOrSmsShareOrApp =
    lowerCaseCurrentSource === URL_SOURCE_TYPE.SMS ||
    lowerCaseCurrentSource === URL_SOURCE_TYPE.CAMPAIGN ||
    lowerCaseCurrentSource === URL_SOURCE_TYPE.MARKETING ||
    lowerCaseCurrentSource === URL_SOURCE_TYPE.SMS_SHARE ||
    lowerCaseCurrentSource === URL_SOURCE_TYPE.APP;
  // const isPfsRepManual = lowerCaseCurrentSource === URL_SOURCE_TYPE.REP_MANUAL;

  const responseCode = responseData?.responseCode;
  const responseMessage = responseData?.responseMessage;
  console.log("responseCode from handleResponseCode function", responseCode);
  switch (responseCode) {
    case 2:
    case 4:
    case 5:
    case 12:
    case 13:
      if (hostUrl && isDigitalOrAgent) {
        sendPostMessageToParentWithData(responseMessage, responseCode);
      } else if (isSmsOrCampaignOrMarketingOrSmsShareOrApp) {
        showPopupWithMessage(
          strings.t_popupTitleError,
          strings.t_popupTextHelpMarketing,
          strings
        );
      } else if (
        lowerCaseCurrentSource === URL_SOURCE_TYPE.PFS_REP ||
        lowerCaseCurrentSource === URL_SOURCE_TYPE.REP_MANUAL ||
        lowerCaseCurrentSource === URL_SOURCE_TYPE.ENSURE
      ) {
        showPopupWithMessage(
          strings.t_popupTitleError,
          strings.t_popupTextHelpLead,
          strings
        );
      }
      break;
    case 6:
      if (isSmsOrCampaignOrMarketingOrSmsShareOrApp) {
        showPopupWithMessage(
          strings.t_popupTitleError,
          strings.eligibiliryErrorPopupUnsupportedDestination,
          strings
        );
      } else if (
        lowerCaseCurrentSource === URL_SOURCE_TYPE.PFS_REP ||
        lowerCaseCurrentSource === URL_SOURCE_TYPE.REP_MANUAL ||
        lowerCaseCurrentSource === URL_SOURCE_TYPE.ENSURE
      ) {
        showPopupWithMessage(
          strings.t_popupTitleError,
          strings.eligibiliryErrorPopupUnsupportedCountry,
          strings
        );
      } else if (hostUrl && isDigitalOrAgent) {
        sendPostMessageToParentWithData(responseMessage, responseCode);
      }
      break;
    case 3:
      if (hostUrl && isDigitalOrAgent) {
        // Send post message with RC=3
        sendPostMessageToParentWithData(responseMessage, responseCode);
      } else if (isSmsOrCampaignOrMarketingOrSmsShareOrApp) {
        // Show error screen for marketing
        showPopupWithMessage(
          strings.t_errorTitleDebt,
          strings.t_errorTextDebtMarketing,
          strings
        );
      } else if (
        lowerCaseCurrentSource === URL_SOURCE_TYPE.PFS_REP ||
        lowerCaseCurrentSource === URL_SOURCE_TYPE.REP_MANUAL ||
        lowerCaseCurrentSource === URL_SOURCE_TYPE.ENSURE
      ) {
        // Show popup for PFS_REP
        showPopupWithMessage(
          strings.t_errorTitleDebt,
          strings.t_errorTextDebtLead,
          strings
        );
      }
      break;

    case 7:
    case 8:
      if (hostUrl && isDigitalOrAgent) {
        // Send post message with RC=7/8
        sendPostMessageToParentWithData(responseMessage, responseCode);
      } else {
        const title = getTitleByResponseCode(responseCode, strings);
        const body = getBodyByResponseCode(responseCode, strings);
        showPopupWithMessage(
          title,
          body,
          strings,
          true,
          0,
          strings.t_error_popup_cant_find_button,
          images.image3,
          images.image3Mobile,
          true,
          strings.t_buy_policy_button
        );
      }

      break;

    case 9:
      if (hostUrl && isDigitalOrAgent) {
        // Send post message with RC=9
        sendPostMessageToParentWithData(responseMessage, responseCode);
      } else if (isSmsOrCampaignOrMarketingOrSmsShareOrApp) {
        // Show popup for missing personal details
        showPopupWithMessage(
          strings.t_popupTitleMissingDetailsMarketing,
          strings.t_popupTextMissingDetailsMarketing,
          strings,
          true,
          0,
          strings.t_error_popup_cant_find_button,
          images.image3,
          images.image3Mobile,
          false,
          strings.t_buy_policy_button
        );
      } else if (lowerCaseCurrentSource === URL_SOURCE_TYPE.PFS_REP) {
        // Show error screen for missing personal details
        showPopupWithMessage(
          strings.t_popupTitleMissingDetailsLead,
          strings.t_popupTextMissingDetailsLead,
          strings
        );
      }
      break;

    case 10:
      if (hostUrl && isDigitalOrAgent) {
        // Send post message with RC=10
        sendPostMessageToParentWithData(responseMessage, responseCode);
      } else {
        // Show popup for age restriction
        showPopupWithMessage(
          strings.t_popupTitleAgeRestriction,
          strings.t_popupTextAgeRestriction,
          strings,
          false,
          0,
          strings.t_error_popup_cant_find_button,
          images.image4,
          images.image4Mobile
        );
      }
      break;

    case 11:
      if (hostUrl && isDigitalOrAgent) {
        // Send post message with RC=11
        sendPostMessageToParentWithData(responseMessage, responseCode);
      } else {
        // Show popup: service not available for annual policyholders
        showPopupWithMessage(
          strings.t_popupTitleServiceNotAvailable,
          strings.t_popupTextServiceNotAvailable,
          strings
        );
      }
      break;

    case 14:
      if (hostUrl && isDigitalOrAgent) {
        // Send post message with enum=14
        sendPostMessageToParentWithData(responseMessage, responseCode);
      } else if (isSmsOrCampaignOrMarketingOrSmsShareOrApp) {
        // Show error screen for already registered
        showPopupWithMessage(
          strings.t_popupTitleAlreadyRegisteredMarketing,
          strings.t_popupTextAlreadyRegisteredMarketing,
          strings
        );
      } else if (
        lowerCaseCurrentSource === URL_SOURCE_TYPE.PFS_REP ||
        lowerCaseCurrentSource === URL_SOURCE_TYPE.REP_MANUAL ||
        lowerCaseCurrentSource === URL_SOURCE_TYPE.ENSURE
      ) {
        // Show error screen for lead already registered
        showPopupWithMessage(
          strings.t_popupTitleAlreadyRegisteredLead,
          strings.t_popupTextAlreadyRegisteredLead,
          strings
        );
      }
      break;
    case 15:
      //sms/campaign/marketing/App/sms-share
      if (isSmsOrCampaignOrMarketingOrSmsShareOrApp) {
        showPopupWithMessage(
          strings.code15MessageTitleType2,
          strings.code15MessageBodyType2,
          strings
        );
      } else if (
        lowerCaseCurrentSource === URL_SOURCE_TYPE.PFS_REP ||
        lowerCaseCurrentSource === URL_SOURCE_TYPE.REP_MANUAL ||
        lowerCaseCurrentSource === URL_SOURCE_TYPE.ENSURE
      ) {
        showPopupWithMessage(
          strings.code15MessageTitleType1,
          strings.code15MessageBodyType1,
          strings
        );
      }

      break;
    default:
      showPopupWithMessage(
        strings.t_login_general_error_title,
        strings.t_login_general_error_text,
        strings,
        false
      );
      break;
  }
};
export const getTaxRefundCondition = (
  status: TaxRefundStatus
): TAX_REFUND_CONDITION => {
  const { isRegistered, isEligible } = status;

  if (!isRegistered && isEligible) {
    return TAX_REFUND_CONDITION.ELIGIBLE_NOT_REGISTERED;
  }
  if (isRegistered && isEligible) {
    return TAX_REFUND_CONDITION.ELIGIBLE_AND_REGISTERED;
  }
  if (isRegistered && !isEligible) {
    return TAX_REFUND_CONDITION.REGISTERED_NOT_ELIGIBLE;
  }
  return TAX_REFUND_CONDITION.NOT_ELIGIBLE;
};

export const checkPathMatch = (
  prefixes: string[],
  suffixes: string[]
): boolean => {
  const path = window.location.pathname; // Get the current URL path

  // Create a regex pattern from the prefixes and suffixes
  const prefixPattern = prefixes.map((p) => `(${p})`).join("|"); // Match any prefix
  const suffixPattern = suffixes.map((s) => `(${s})`).join("|"); // Match any suffix

  // Regex: Ensures path is exactly {prefix}/{suffix}, with no extra segments
  const regex = new RegExp(`^(/${prefixPattern})/(${suffixPattern})/?$`);

  return regex.test(path);
};

// פונקציה לזיהוי אם ההטעמה היא בהברה האחרונה
const isFinalStress = (word: string): boolean => {
  // כאן אפשר להוסיף בדיקה חכמה יותר אם יש בסיס מורפולוגי
  return (
    word.length > 2 && ["ות", "יה"].some((suffix) => word.endsWith(suffix))
  );
};

export const getPluralForm = (noun: string, gender: Gender): string => {
  // שמות קיבוציים לרוב לא משתנים ברבים
  if (gender === Gender.COLLECTIVE) return noun;

  // אם שם עצם בנקבה מסתיים ב-"ה", מחליפים את "ה" ב-"ות"
  if (gender === Gender.FEMALE && noun.endsWith("ה")) {
    return noun.slice(0, -1) + "ות";
  }

  // אם שם עצם בנקבה מסתיים ב-"יה", מחליפים את "ה" ב-"יות"
  if (gender === Gender.FEMALE && noun.endsWith("יה")) {
    return noun.slice(0, -1) + "ות";
  }

  // אם שם עצם בנקבה מסתיים ב-"ות" וההטעמה לא בסוף, מוסיפים "י" לפני "ות"
  if (gender === Gender.FEMALE && noun.endsWith("ות") && !isFinalStress(noun)) {
    return noun.slice(0, -1) + "יות";
  }

  // אם שם עצם בזכר מסתיים ב-"ן", מחליפים את "ן" ב-"נים"
  if (gender === Gender.MALE && noun.endsWith("ן")) {
    return noun.slice(0, -1) + "נים";
  }

  // שמות בזכר שמסתיימים ב-"ל" או "ר" מקבלים "-ים"
  if (gender === Gender.MALE && (noun.endsWith("ל") || noun.endsWith("ר"))) {
    return noun + "ים";
  }

  // ברירת מחדל: בזכר מוסיפים "ים", בנקבה מוסיפים "ות"
  return gender === Gender.MALE ? noun + "ים" : noun + "ות";
};

export const formatNumberWithNoun = (
  num: number,
  noun: HebrowNouns
): string => {
  const maleNumbers = [
    "אחד",
    "שניים",
    "שלושה",
    "ארבעה",
    "חמישה",
    "שישה",
    "שבעה",
    "שמונה",
    "תשעה",
    "עשרה",
  ];
  const femaleNumbers = [
    "אחת",
    "שתיים",
    "שלוש",
    "ארבע",
    "חמש",
    "שש",
    "שבע",
    "שמונה",
    "תשע",
    "עשר",
  ];
  const gender = HebrewNounsGenderMap[noun] || Gender.MALE;
  // התאמת המספר למגדר השם
  const numWord =
    num >= 1 && num <= 10
      ? gender === Gender.MALE
        ? maleNumbers[num - 1]
        : femaleNumbers[num - 1]
      : num.toString();

  return `${noun} מספר ${numWord}`;
};

// פונקציה להמרת מספרים בעברית (כולל 1 עד 999) עם תמיכה בזכר/נקבה
export const formatNumberInHebrew = (
  num: number,
  noun: HebrowNouns
): string => {
  const gender = HebrewNounsGenderMap[noun] || Gender.MALE;
  const formattedNoun = getPluralForm(noun, gender);
  if (num === 1) return `${unitNumbers[gender][0]} ${noun}`;
  if (num === 2) return `${unitNumbers[gender][1]} ${formattedNoun}`;
  if (num <= 9) return `${unitNumbers[gender][num - 1]} ${formattedNoun}`;
  if (num <= 10) return `${tensNumbers[0]} ${formattedNoun}`;
  if (num < 100) {
    const tens = Math.floor(num / 10);
    const units = num % 10;
    return units === 0
      ? `${tensNumbers[tens - 1]} ${formattedNoun}`
      : `${tensNumbers[tens - 1]} ו${
          unitNumbers[gender][units - 1]
        } ${formattedNoun}`;
  }
  if (num < 1000) {
    const hundreds = Math.floor(num / 100);
    const remainder = num % 100;
    return remainder === 0
      ? `${hundredsNumbers[hundreds - 1]} ${formattedNoun}`
      : `${hundredsNumbers[hundreds - 1]} ו${formatNumberInHebrew(
          remainder,
          noun
        )}`;
  }
  return `${num} ${formattedNoun}`; // עבור 1000 ומעלה נשארים מספרים רגילים
};

export const formatNumberInHebrewInverted = (
  num: number,
  noun: HebrowNouns
): string => {
  const text = formatNumberInHebrew(num, noun);
  return text.split(" ").reverse().join(" ");
};

// פונקציה להמרת תאריכים לצורה תקינה בעברית
const formatOrdinalDate = (day: number, month: number): string => {
  return `מה${ordinalNumbers[day]} ל${monthNames[month]}`;
};

// פונקציה להמרת טווח תאריכים בעברית תקינה
export const formatDateRange = (dates: string) => {
  const parts = dates.split(" - ");
  if (parts.length === 2) {
    const [startDay, startMonth] = parts[0].split("/").map(Number);
    const [endDay, endMonth] = parts[1].split("/").map(Number);
    return `${formatOrdinalDate(startDay, startMonth)} ועד ל${
      ordinalNumbers[endDay]
    } ל${monthNames[endMonth]}`;
  }
  return dates; // אם הפורמט לא תקין
};

// טיפול ביעדים עם אותיות גרוניות (לארגנטינה, לברזיל וכו')
export const formatDestination = (destination: string) => {
  const gutturalLetters = ["א", "ה", "ח", "ע", "ר"];
  return gutturalLetters.includes(destination.charAt(0))
    ? `ל${destination}`
    : `ל${destination}`;
};

export const parseResponseError = <T>(error: any, type: T): T => {
  try {
    return JSON.parse(error.message || "{}") as T;
  } catch {
    // If parsing fails, return a default object of type T
    return {} as T;
  }
};
export const logPfsAnalyticsEvent = async (data: UpdateOnboardingAnalytics) => {
  const currentSource = window.location.pathname
    .split("/")[1]
    .toLocaleLowerCase();
  const defaultData: UpdateOnboardingAnalytics = {
    fePfsSessionId: store.getState().app.sessionId,
    feCustPerslId: store.getState().app.personalId || "",
    feCustTravelPolicyNumber: store.getState().app.travelPolicyNumber,
    feBu: store.getState().app.businessUnit,
    feChannel: getChannelId(store.getState().app.hostUrl),
    feUserPlatform: isMobile()
      ? UserPlatform.WEB_MOBILE
      : isDesktop()
      ? UserPlatform.WEB_DESKTOP
      : UserPlatform.WEB_TABLET,
    feCreatedBy: getChannelId(
      new URLSearchParams(window.location.search).get("createdBy") ||
        store.getState().app.createdBy ||
        ""
    ),
  };

  if (currentSource === URL_SOURCE_TYPE.CAMPAIGN) {
    defaultData.campaign_id =
      new URLSearchParams(window.location.search).get("campaignId") || "";
    defaultData.campaign_name =
      new URLSearchParams(window.location.search).get("campaignName") || "";
    defaultData.campaign_source =
      new URLSearchParams(window.location.search).get("campaignSource") || "";
    defaultData.campaign_medium =
      new URLSearchParams(window.location.search).get("campaignMedium") || "";
    defaultData.campaign_term =
      new URLSearchParams(window.location.search).get("campaignTerm") || "";
    defaultData.campaign_content =
      new URLSearchParams(window.location.search).get("campaignContent") || "";
  } else if (currentSource === URL_SOURCE_TYPE.DIGITAL) {
    const experimentData = store.getState().app.experimentData;
    if (experimentData) {
      defaultData.sales_experiment_id = experimentData.salesExperimentID;
      defaultData.experiment_name = experimentData.experimentName;
      defaultData.sales_experiment_version_id =
        experimentData.salesExperimentVersionID;
      defaultData.version_name = experimentData.versionName;
      defaultData.experiment_type = experimentData.experimentType;
    }
  }
  const updatedData = { ...defaultData, ...data };
  console.log("< ===== Analytics Event === > ", data);
  // if mock data is enabled, don't log the event

  if (!store.getState().app.mockDataEnabled) {
    // if (updatedData.abandon) {
    //   await updateAnaliticsWithKeepAlive(updatedData);
    // } else
    // {
    //   await store.dispatch(updateOnboardingAnalytics(updatedData));
    // }
    await store.dispatch(updateOnboardingAnalytics(updatedData));
  }
};
export const getUrlParams = () => {
  // get all the params from the url
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.toString();
};
